import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { signupThunk } from '../redux/userSlice'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Input } from '../components/ui/input'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader } from '../components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { X } from 'lucide-react'
import { cn } from '../lib/utils'
import api, { checkExistingAthlete, findSchoolForEmail, type SchoolOption } from '../lib/api'
import SportsMultiSelect from '../components/SportsMultiSelect'
import SchoolPicker from '../components/SchoolPicker'
import { AppDispatch, RootState } from '../store'
import { Checkbox } from '../components/ui/checkbox'
import ExistingAccountDialog from '../components/ExistingAccountDialog'
import { CURRENT_LEGAL_VERSION } from '@adthlete/shared-frontend'
import { checkSocialHandle } from '@adthlete/common/utils/socialHandle'

const useAppDispatch = () => useDispatch<AppDispatch>()

const MINIMUM_ATHLETE_AGE = 18

function calculateAge(dateOfBirth: string): number {
  const dob = new Date(dateOfBirth)
  const now = new Date()
  let age = now.getFullYear() - dob.getFullYear()
  const monthDiff = now.getMonth() - dob.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age -= 1
  }
  return age
}

const GOOGLE_SIGNUP_KEY = 'googleSignupToken'

/** Split a full name into first name and the rest (Google names, older drafts) */
function splitName(fullName: string): [string, string] {
  const [first = '', ...rest] = String(fullName).trim().split(/\s+/)
  return [first, rest.join(' ')]
}

const Onboarding: React.FC = () => {
  // Someone who signed in with Google and has no account yet lands here with a signup token in
  // the URL fragment. Google already confirmed their email, so there is no password step.
  const [googleToken] = useState<string | null>(() => {
    const fromUrl = window.location.hash.match(/google_signup=([^&]+)/)?.[1] ?? null
    try {
      if (fromUrl) {
        sessionStorage.setItem(GOOGLE_SIGNUP_KEY, fromUrl)
        window.history.replaceState(null, '', window.location.pathname)
      }
      return sessionStorage.getItem(GOOGLE_SIGNUP_KEY)
    } catch {
      return fromUrl
    }
  })
  const [googleEmail, setGoogleEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [existingEmail, setExistingEmail] = useState<string | null>(null)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [selectedSchool, setSelectedSchool] = useState<SchoolOption | null>(null)
  // The school their email address belongs to, picked for them if they had none yet
  const [emailSchool, setEmailSchool] = useState<SchoolOption | null>(null)
  const [selectedSports, setSelectedSports] = useState<number[]>([])
  const [socialHandles, setSocialHandles] = useState<{ platform: string; handle: string }[]>([])
  // Platforms whose handle the athlete has finished with (left the field, or tried to submit).
  // A handle's problem is only shown after that, so nobody is told off mid-typing.
  const [handlesChecked, setHandlesChecked] = useState<string[]>([])
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [error, setError] = useState('')
  const dispatch = useAppDispatch()
  const isLoading = useSelector((state: RootState) => state.user.loading)
  const navigate = useNavigate()
  const { athlete } = useAuth()

  // If already signed in, redirect to home
  useEffect(() => {
    if (athlete) {
      navigate('/', { replace: true })
    }
  }, [athlete, navigate])

  // Check if we have signup data from the sign-up form
  useEffect(() => {
    if (googleToken) return
    const signupEmail = sessionStorage.getItem('signupEmail')
    const signupPassword = sessionStorage.getItem('signupPassword')

    if (!signupEmail || !signupPassword) {
      // If no signup data, redirect to sign-in page
      navigate('/signin')
    }
  }, [navigate, googleToken])

  // Show whose Google account this is, and start with their name
  useEffect(() => {
    if (!googleToken) return
    api
      .get('/users/oauth/signup-info', { params: { token: googleToken } })
      .then((res) => {
        setGoogleEmail(res.data?.email || '')
        if (res.data?.name) {
          const [first, last] = splitName(res.data.name)
          setFirstName((current) => current || first)
          setLastName((current) => current || last)
        }
      })
      .catch(() => {
        try {
          sessionStorage.removeItem(GOOGLE_SIGNUP_KEY)
        } catch {}
        navigate('/signin?oauth_error=expired', { replace: true })
      })
  }, [googleToken, navigate])

  // Load saved form data from sessionStorage
  useEffect(() => {
    const savedForm = sessionStorage.getItem('onboardingFormData')
    if (savedForm) {
      try {
        const parsed = JSON.parse(savedForm)
        if (parsed.firstName) setFirstName(parsed.firstName)
        if (parsed.lastName) setLastName(parsed.lastName)
        // Drafts saved before first and last name were separate
        if (parsed.fullName && !parsed.firstName) {
          const [first, last] = splitName(parsed.fullName)
          setFirstName(first)
          setLastName(last)
        }
        if (parsed.dateOfBirth) setDateOfBirth(parsed.dateOfBirth)
        if (parsed.selectedSports) setSelectedSports(parsed.selectedSports)
        if (parsed.socialHandles) setSocialHandles(parsed.socialHandles)
        if (parsed.agreedToTerms) setAgreedToTerms(parsed.agreedToTerms)
        if (parsed.selectedSchool) setSelectedSchool(parsed.selectedSchool)
      } catch (e) {
        console.error('Failed to parse saved form data', e)
      }
    }
  }, [])

  // Save form data to sessionStorage on change
  useEffect(() => {
    const formData = {
      firstName,
      lastName,
      dateOfBirth,
      selectedSports,
      socialHandles,
      agreedToTerms,
      selectedSchool,
    }
    sessionStorage.setItem('onboardingFormData', JSON.stringify(formData))
  }, [
    firstName,
    lastName,
    dateOfBirth,
    selectedSports,
    socialHandles,
    agreedToTerms,
    selectedSchool,
  ])

  // Signing up with a school email (name@umd.edu, name@terpmail.umd.edu): start with that school
  const signupEmail = googleToken ? googleEmail : sessionStorage.getItem('signupEmail') || ''
  useEffect(() => {
    if (!signupEmail || selectedSchool) return
    let cancelled = false
    findSchoolForEmail(signupEmail).then((school) => {
      if (cancelled || !school) return
      setEmailSchool(school)
      setSelectedSchool((current) => current ?? school)
    })
    return () => {
      cancelled = true
    }
    // Only when the email becomes known; a school they pick themselves is never replaced
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signupEmail])

  const availablePlatforms = ['Instagram', 'TikTok', 'YouTube']
  const usedPlatforms = socialHandles.map((sh) => sh.platform)
  const remainingPlatforms = availablePlatforms.filter((p) => !usedPlatforms.includes(p))

  const schoolFromEmail = emailSchool !== null && selectedSchool?.id === emailSchool.id

  const fullName = `${firstName.trim()} ${lastName.trim()}`.trim()

  const isFormValid =
    firstName.trim() !== '' &&
    lastName.trim() !== '' &&
    dateOfBirth !== '' &&
    selectedSchool !== null &&
    selectedSports.length > 0 &&
    socialHandles.length > 0 &&
    socialHandles.every((sh) => sh.handle.trim() !== '') &&
    agreedToTerms

  const handleProblem = (social: { platform: string; handle: string }) =>
    handlesChecked.includes(social.platform) ? checkSocialHandle(social.handle) : null

  const addSocialHandle = (platform: string) => {
    setSocialHandles([...socialHandles, { platform, handle: '' }])
  }

  const updateSocialHandle = (index: number, handle: string) => {
    const updated = [...socialHandles]
    updated[index].handle = handle
    setSocialHandles(updated)
  }

  const removeSocialHandle = (index: number) => {
    setSocialHandles(socialHandles.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e: React.FormEvent, skipCheck = false) => {
    e.preventDefault()
    setError('')

    // Show what is wrong with a handle and take them to it, rather than a vague message
    const badHandle = socialHandles.find((sh) => checkSocialHandle(sh.handle))
    if (badHandle) {
      setHandlesChecked(socialHandles.map((sh) => sh.platform))
      document.getElementById(`handle-${badHandle.platform}`)?.focus()
      return
    }

    if (!isFormValid) {
      setError('Please complete all required fields')
      return
    }

    if (calculateAge(dateOfBirth) < MINIMUM_ATHLETE_AGE) {
      setError(
        `You must be at least ${MINIMUM_ATHLETE_AGE} years old to create an athlete account`,
      )
      return
    }

    // Someone with this name and birthday may already have an account: offer sign in first
    if (!skipCheck) {
      setSubmitting(true)
      const masked = await checkExistingAthlete(fullName, dateOfBirth)
      setSubmitting(false)
      if (masked) {
        setExistingEmail(masked)
        return
      }
    }

    if (googleToken) {
      setSubmitting(true)
      try {
        await api.post('/users/oauth/google/register', {
          signup_token: googleToken,
          name: fullName,
          school_id: selectedSchool?.id || null,
          sports: selectedSports,
          socialHandles,
          date_of_birth: dateOfBirth,
          agreed_to_terms: agreedToTerms,
          legal_version: CURRENT_LEGAL_VERSION,
        })
        sessionStorage.removeItem('onboardingFormData')
        sessionStorage.removeItem(GOOGLE_SIGNUP_KEY)
        sessionStorage.setItem('socialHandles', JSON.stringify(socialHandles))
        // The session cookie is set; a full load picks the signed-in user up. Load the app's own
        // root (it may live under a path such as /athlete), and the route guard sends a new athlete
        // on to their welcome
        window.location.assign(import.meta.env.BASE_URL)
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Sign-up failed. Please try again.')
        setSubmitting(false)
      }
      return
    }

    const signupEmail = sessionStorage.getItem('signupEmail')
    const signupPassword = sessionStorage.getItem('signupPassword')

    if (!signupEmail || !signupPassword) {
      setError('Session expired. Please sign up again.')
      navigate('/signin')
      return
    }

    try {
      const result = await dispatch(
        signupThunk({
          name: fullName,
          email: signupEmail,
          password: signupPassword,
          phone: '', // Optional field
          school_id: selectedSchool?.id || null,
          sports: selectedSports,
          socialHandles,
          date_of_birth: dateOfBirth,
          agreed_to_terms: agreedToTerms,
          legal_version: CURRENT_LEGAL_VERSION,
        }),
      ).unwrap()

      if (result) {
        // Store social handles for later use (could be sent to backend in future)
        sessionStorage.setItem('socialHandles', JSON.stringify(socialHandles))

        // Clear the stored signup data and form data
        sessionStorage.removeItem('onboardingFormData')
        sessionStorage.removeItem('signupEmail')
        sessionStorage.removeItem('signupEmail')
        sessionStorage.removeItem('signupPassword')
        // Store email for the verification page
        sessionStorage.setItem('userEmailForVerification', signupEmail)
        // Redirect to the email verification pending page
        navigate('/verify-email', { state: { email: signupEmail } })
      }
    } catch (err: unknown) {
      const error = err as { message?: string }
      setError(error.message || 'Registration failed. Please try again.')
    }
  }

  const handleSportsChange = (sportIds: number[]) => {
    setSelectedSports(sportIds)
  }

  return (
    <div className="flex items-center justify-center min-h-screen relative">
      <div className="fixed inset-0 -z-10 bg-background"></div>

      <ExistingAccountDialog
        maskedEmail={existingEmail}
        name={fullName}
        dateOfBirth={dateOfBirth}
        onClose={() => setExistingEmail(null)}
        onContinue={() => {
          setExistingEmail(null)
          void handleSubmit({ preventDefault() {} } as React.FormEvent, true)
        }}
      />

      <div className="w-full max-w-md px-4">
        <Card variant="dark">
          <CardHeader className="text-center pb-0">
            <div className="flex justify-center mb-6">
              <img
                src="https://adthlete.us-iad-1.linodeobjects.com/public/adthlete_logomark.png"
                alt="Athios Logo"
                className="w-10 h-10"
              />
            </div>
            <CardDescription className="mt-2 text-gray-600">
              Complete your Athios profile
            </CardDescription>
            {googleEmail && (
              <p className="mt-1 text-sm text-gray-600">Signing up as {googleEmail}</p>
            )}
          </CardHeader>
          <CardContent className="pt-6">
            {error && <p className="text-red-500 mb-4 text-sm text-center">{error}</p>}
            <form onSubmit={handleSubmit} data-track-form="Sign-up details" className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Input
                  type="text"
                  placeholder="First name *"
                  aria-label="First name"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="min-w-0 bg-card text-base text-foreground border-foreground/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                  required
                />
                <Input
                  type="text"
                  placeholder="Last name *"
                  aria-label="Last name"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="min-w-0 bg-card text-base text-foreground border-foreground/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                  required
                />
              </div>

              <div>
                <label htmlFor="date-of-birth" className="text-sm text-gray-600">
                  Date of Birth *
                </label>
                <Input
                  id="date-of-birth"
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="mt-2 bg-card text-foreground border-foreground/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                  required
                />
              </div>

              <div>
                <label htmlFor="school" className="text-sm text-gray-600">
                  School *
                </label>
                <SchoolPicker
                  id="school"
                  value={selectedSchool}
                  onChange={setSelectedSchool}
                  aria-describedby={schoolFromEmail ? 'school-from-email' : undefined}
                  className="mt-2"
                />
                {schoolFromEmail && (
                  <p id="school-from-email" className="mt-1.5 text-xs text-gray-600">
                    Matched from your email. Not right? Tap to change it.
                  </p>
                )}
              </div>

              <SportsMultiSelect
                label="Sports *"
                description="What sports do you participate in?"
                selectedSportIds={selectedSports}
                onSportsChange={handleSportsChange}
                placeholder="Add a sport..."
              />

              <div className="space-y-2">
                <label className="text-sm text-gray-600">
                  Social Handles (at least 1 required) *
                </label>

                {socialHandles.map((social, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-card border border-foreground/10 rounded-md px-3 py-2 text-foreground text-sm">
                        {social.platform}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeSocialHandle(index)}
                        className="p-2 hover:bg-foreground/5 rounded-md transition-colors"
                      >
                        <X className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                    <Input
                      id={`handle-${social.platform}`}
                      type="text"
                      placeholder={`@${social.platform.toLowerCase()}`}
                      value={social.handle}
                      onChange={(e) => updateSocialHandle(index, e.target.value)}
                      onBlur={() =>
                        setHandlesChecked((checked) =>
                          checked.includes(social.platform)
                            ? checked
                            : [...checked, social.platform],
                        )
                      }
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      aria-invalid={handleProblem(social) ? true : undefined}
                      aria-describedby={
                        handleProblem(social) ? `handle-${social.platform}-error` : undefined
                      }
                      className={cn(
                        'bg-card text-foreground border-foreground/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20',
                        handleProblem(social) && 'border-red-500/60 focus:border-red-500',
                      )}
                      required
                    />
                    {handleProblem(social) && (
                      <p id={`handle-${social.platform}-error`} className="text-xs text-red-600">
                        {handleProblem(social)}
                      </p>
                    )}
                  </div>
                ))}

                {remainingPlatforms.length > 0 && (
                  <Select key={socialHandles.length} onValueChange={addSocialHandle}>
                    <SelectTrigger className="bg-card text-foreground border-foreground/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20">
                      <SelectValue
                        placeholder={
                          socialHandles.length === 0 ? 'Select social platform' : 'Add another'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-foreground/10 text-foreground">
                      {remainingPlatforms.map((platform) => (
                        <SelectItem
                          key={platform}
                          value={platform}
                          className="focus:bg-foreground/10 focus:text-foreground"
                        >
                          {platform}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="flex items-start space-x-3 my-4">
                <Checkbox
                  id="agree-terms"
                  checked={agreedToTerms}
                  onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
                  className="border-foreground/20 data-[state=checked]:bg-[#C0FD72] data-[state=checked]:border-[#C0FD72] mt-1"
                />
                <label
                  htmlFor="agree-terms"
                  className="text-sm text-gray-700 leading-relaxed cursor-pointer"
                >
                  I agree to the{' '}
                  <Link to="/terms" className="text-[#4d7c0f] hover:text-[#a8e45a] underline">
                    Terms of Use and Privacy Policy
                  </Link>{' '}
                  outlined below. I understand that I am responsible for complying with all
                  applicable NIL regulations and institutional policies.
                </label>
              </div>

              <Button
                type="submit"
                className={`w-full font-medium transition-all ${
                  isFormValid
                    ? 'bg-[#C0FD72] hover:bg-[#a8e45a] text-black'
                    : 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-50'
                }`}
                disabled={!isFormValid || isLoading || submitting}
              >
                {isLoading || submitting ? 'Creating Account...' : 'Complete Registration'}
              </Button>

              <div className="text-center">
                <Link
                  to="/signin"
                  className="text-sm text-gray-600 hover:text-[#4d7c0f] transition-colors"
                >
                  Already have an account? Sign in
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default Onboarding
